import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchLawyerBookings, updateLawyerBookingStatus } from "../services/bookingService";

const BookingContext = createContext(null);

export const BookingProvider = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadBookings = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    setError("");
    try {
      const list = await fetchLawyerBookings();
      setBookings(list);
    } catch (err) {
      setError(err?.message || "Failed to load bookings");
      setBookings([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      loadBookings({ silent: true });
    };

    const handleBookingUpdated = () => {
      loadBookings({ silent: true });
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("lawyer:booking-updated", handleBookingUpdated);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("lawyer:booking-updated", handleBookingUpdated);
    };
  }, []);

  const value = useMemo(
    () => ({
      bookings,
      selectedBooking,
      setSelectedBooking,
      filter,
      setFilter,
      loading,
      error,
      refreshBookings: loadBookings,
      getFilteredBookings: (status) => {
        if (!status || status === "all") return bookings;
        return bookings.filter((item) => String(item.status || "").toLowerCase() === String(status).toLowerCase());
      },
      updateBookingStatus: (bookingId, newStatus) => {
        setBookings((prev) =>
          prev.map((item) => (String(item.id) === String(bookingId) ? { ...item, status: newStatus } : item))
        );
      },
      respondToBooking: async (bookingId, decision) => {
        const updated = await updateLawyerBookingStatus(bookingId, decision);
        setBookings((prev) =>
          prev.map((item) => (String(item.id) === String(bookingId) ? { ...item, ...updated } : item))
        );
        await loadBookings();
        window.dispatchEvent(new CustomEvent("lawyer:booking-updated"));
        return updated;
      },
    }),
    [bookings, filter, loading, error, selectedBooking]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBookings = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBookings must be used within BookingProvider");
  }
  return context;
};
